import { useNavigate, useLocation } from "react-router-dom";
import { useState, useMemo } from "react";
import { Box, Card, CardContent, Typography, IconButton, Snackbar, Alert } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import StarIcon from '@mui/icons-material/Star';
import StarHalfIcon from '@mui/icons-material/StarHalf';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import FavoriteIcon from '@mui/icons-material/Favorite';
import CustomDivider from "../utils/CustomDivider";
import { postWishlist } from "../../assets/nodeApi/postApi/postCommonApi";
import { deleteWishlist } from "../../assets/nodeApi/deleteApi/deleteAPi";
import { useSkillBoxWishlist } from "../../assets/nodeApi/getApi/getCommonApi";


/** Resolve image URL — supports both full URLs and bare filenames */
const resolveImage = (image_small) => {
    if (!image_small) return "";
    if (image_small.startsWith("http://") || image_small.startsWith("https://")) {
        return image_small;
    }
    return `https://myskill.club/shop/uploads/images/${image_small}`;
};

/** Render 5 star icons based on a numeric rating (e.g. 4.7) */
const StarRating = ({ rating = 0, totalReviews }) => {
    const stars = Array.from({ length: 5 }, (_, i) => {
        const filled = i + 1 <= Math.floor(rating);
        const half = !filled && i < rating;
        return filled ? "full" : half ? "half" : "empty";
    });

    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
            {stars.map((type, i) => (
                type === "full" ? (
                    <StarIcon key={i} sx={{ fontSize: "16px", color: "#F5A623" }} />
                ) : type === "half" ? (
                    <StarHalfIcon key={i} sx={{ fontSize: "16px", color: "#F5A623" }} />
                ) : (
                    <StarBorderIcon key={i} sx={{ fontSize: "16px", color: "#F5A623" }} />
                )
            ))}
            {totalReviews !== undefined && (
                <Typography variant="caption" color="grey.500" sx={{ ml: 0.5 }}>
                    ({totalReviews})
                </Typography>
            )}
        </Box>
    );
};

const SkillBoxProductCard = ({ item, sourcePath: customSourcePath, sourceName: customSourceName }) => {
    const imageUrl = resolveImage(item?.image_small);
    const navigate = useNavigate();
    const location = useLocation();
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const queryClient = useQueryClient();

    const { data: wishlistData } = useSkillBoxWishlist();
    const isFavorite = useMemo(() => {
        if (!wishlistData?.data) return false;
        return wishlistData.data.some(w => String(w.product_id || w.productId || w.id) === String(item?.id));
    }, [wishlistData, item?.id]);

    let sourcePath = customSourcePath;
    let sourceName = customSourceName;

    if (sourcePath === undefined || sourceName === undefined) {
        sourcePath = '/SkilltoPreskool';
        sourceName = 'Skill To Preskool';

        if (location.pathname === '/SkillBox') {
            sourcePath = '/SkillBox';
            sourceName = 'Skill Box';
        } else if (location.pathname === '/SkilltoPreskool') {
            sourcePath = '/SkilltoPreskool';
            sourceName = 'Skill To Preskool';
        } else if (location.state?.sourcePath) {
            sourcePath = location.state.sourcePath;
            sourceName = location.state.sourceName;
        }
    }

    return (
        <Card
            sx={{
                borderRadius: "15px",
                boxShadow: "0 0 0 0 rgba(0,0,0,0)",
                overflow: "hidden",
                "&:hover": {
                    transform: "translateY(-3px)",
                    boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
                },
            }}
        >
            {/* ── Product Image ── */}
            <CardContent
                sx={{
                    position: "relative",
                    backgroundColor: "#eee",
                    p: 2,
                    "&:last-child": { pb: 0 },
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                }}
                onClick={() => {
                    navigate(`/ProductDetails/${item?.id || item?.slug}`, { state: { sourcePath, sourceName } });
                }}
            >
                <Box
                    component="img"
                    src={imageUrl}
                    sx={{
                        width: "72%",
                        height: "72%",
                        objectFit: "contain",
                        borderRadius: "0px",
                    }}
                />
                {/* Discount badge */}
                {/* {item?.price && item?.price_discounted && (
                    <Box
                        sx={{
                            position: "absolute",
                            top: 10,
                            left: 10,
                            bgcolor: "#DC143C",
                            color: "#fff",
                            px: 1,
                            py: 0.25,
                            borderRadius: "6px",
                            fontSize: "11px",
                            fontWeight: 700,
                            letterSpacing: 0.3,
                        }}
                    >
                        {Math.round(
                            ((parseFloat(item.price) - parseFloat(item.price_discounted)) /
                                parseFloat(item.price)) *
                            100
                        )}% OFF
                    </Box>
                )} */}

                {/* Favourite Icon — top right */}
                <IconButton
                    onClick={(e) => {
                        e.stopPropagation();
                        let isAuthenticated = false;
                        try {
                            const authStorage = JSON.parse(localStorage.getItem('auth-storage'));
                            isAuthenticated = !!authStorage?.state?.accessToken;
                        } catch (e) {
                            console.error("Error reading auth storage", e);
                        }

                        if (isAuthenticated) {
                            if (isFavorite) {
                                deleteWishlist({ productId: item?.id })
                                    .then(() => queryClient.invalidateQueries({ queryKey: ["v1/wishlist/items"] }))
                                    .catch(err => console.error(err));
                            } else {
                                postWishlist({ productId: item?.id })
                                    .then(() => queryClient.invalidateQueries({ queryKey: ["v1/wishlist/items"] }))
                                    .catch(err => console.error(err));
                            }
                        } else {
                            setSnackbarOpen(true);
                        }
                    }}
                    sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        bgcolor: isFavorite ? "#e3e3e3" : "#cfcfcf",
                        backdropFilter: "blur(4px)",
                        width: 34,
                        height: 34,
                        transition: "transform 0.2s ease, background-color 0.2s ease",
                        "&:hover": {
                            bgcolor: isFavorite ? "#e3e3e3" : "#cfcfcf",
                            transform: "scale(1.05)",
                        },
                        "&:active": {
                            transform: "scale(0.9)",
                        },
                    }}
                >
                    {isFavorite ? (
                        <FavoriteIcon sx={{ fontSize: "18px", color: "#DC143C" }} />
                    ) : (
                        <FavoriteIcon sx={{ fontSize: "18px", color: "#FFF" }} />
                    )}
                </IconButton>
            </CardContent>

            {/* ── Card Body ── */}
            <CardContent
                sx={{
                    border: "1px solid #E7E7E7",
                    borderTop: "none",
                    background: "#FFFFFF",
                    px: "1rem",
                    pt: "0.85rem",
                    pb: "1rem !important",
                    borderBottomLeftRadius: "15px",
                    borderBottomRightRadius: "15px",
                }}
            >
                {/* Row 1: Course Name (left, 1 line marquee on hover) + Discounted Price (right) */}
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                    {/* Course name with marquee on hover */}
                    <Box
                        sx={{
                            flex: 1,
                            overflow: "hidden",
                            minWidth: 0,
                            "&:hover .marquee-text": {
                                animation: "marqueeScroll 4s linear infinite",
                            },
                            "@keyframes marqueeScroll": {
                                "0%": { transform: "translateX(0%)" },
                                "100%": { transform: "translateX(-100%)" },
                            },
                        }}
                    >
                        <Typography
                            className="marquee-text"
                            variant="body2"
                            fontWeight={600}
                            noWrap
                            sx={{
                                display: "inline-block",
                                whiteSpace: "nowrap",
                                transition: "none",
                            }}
                        >
                            {item?.course_name}
                        </Typography>
                    </Box>

                    {/* Discounted price — right side */}
                    {item?.price !== item?.price_discounted && (
                        <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0, mb: 0.5 }}>
                            <CurrencyRupeeIcon sx={{ color: "error.main", fontSize: "15px" }} />
                            <Typography variant="body2" fontWeight={600}>
                                {item?.price_discounted}
                            </Typography>
                        </Box>
                    )}
                </Box>

                <CustomDivider size={"0.35rem"} />

                {/* Row 2: Star Rating (left) + Original price strikethrough (right) */}
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 0.2 }}>
                    {/* Star Rating */}
                    <StarRating
                        rating={item?.rating}
                        totalReviews={item?.total_reviews}
                    />

                    {/* Original price (strikethrough) — right side */}
                    {item?.price && item?.price !== item?.price_discounted && (
                        <Typography
                            variant="caption"
                            color="grey.400"
                            sx={{ textDecoration: "line-through", flexShrink: 0 }}
                        >
                            ₹ {item?.price}
                        </Typography>
                    )}
                    {item?.price === item?.price_discounted && (
                        <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
                            <CurrencyRupeeIcon sx={{ color: "error.main", fontSize: "15px" }} />
                            <Typography variant="body2" fontWeight={600}>
                                {item?.price_discounted}
                            </Typography>
                        </Box>
                    )}
                </Box>

                {/* Shop Now Button */}
                {/* <CustomButtonContained
                    buttonText="Shop Now"
                    onClick={() => {
                        window.open(`${Main_URL}shop/${item?.slug}`, "_blank");
                    }}
                /> */}
            </CardContent>

            <Snackbar 
                open={snackbarOpen} 
                autoHideDuration={4000} 
                onClose={() => setSnackbarOpen(false)} 
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                onClick={(e) => e.stopPropagation()}
            >
                <Alert onClose={() => setSnackbarOpen(false)} severity="info" sx={{ width: '100%' }}>
                    Please login and buy products or add your favorite items to wishlist
                </Alert>
            </Snackbar>

        </Card>
    );
};

export default SkillBoxProductCard;
